import type { LipDetection } from '../tracking/faceLandmarker';
import type { MakeupStyle } from '../catalog/catalog';

export interface FaceMorphologyTuning {
  cheekOpacityScale: number;
  cheekRadiusScale: number;
  cheekDrapeScale: number;
  eyeOpacityScale: number;
  eyeShapeTightness: number;
  eyeFeatherScale: number;
  eyeSparkleScale: number;
  lipOpacityScale: number;
  lipInnerHoleScale: number;
  lipFinishScale: number;
}

export interface FaceMorphologyReport {
  eyeAperture: 'narrow' | 'balanced' | 'open';
  lidSpace: 'compact' | 'balanced' | 'roomy';
  lipFullness: 'thin' | 'balanced' | 'full';
  cheekSpace: 'compact' | 'balanced' | 'broad';
  tuning: FaceMorphologyTuning;
}

export interface LookMorphologyTuningReport {
  rules: string[];
  tuning: FaceMorphologyTuning;
}

interface Point {
  x: number;
  y: number;
}

const DEFAULT_TUNING: FaceMorphologyTuning = {
  cheekOpacityScale: 1,
  cheekRadiusScale: 1,
  cheekDrapeScale: 1,
  eyeOpacityScale: 1,
  eyeShapeTightness: 0,
  eyeFeatherScale: 1,
  eyeSparkleScale: 1,
  lipOpacityScale: 1,
  lipInnerHoleScale: 1,
  lipFinishScale: 1
};

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function point(detection: LipDetection, index: number): Point | null {
  const p = detection.landmarks[index];
  if (!p) return null;
  return {
    x: p.x * detection.imageWidth,
    y: p.y * detection.imageHeight
  };
}

function distance(a: Point | null, b: Point | null): number | null {
  if (!a || !b) return null;
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function average(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function faceWidth(detection: LipDetection): number {
  return distance(point(detection, 234), point(detection, 454)) ?? detection.imageWidth * 0.48;
}

function faceHeight(detection: LipDetection): number {
  return distance(point(detection, 10), point(detection, 152)) ?? detection.imageHeight * 0.62;
}

function eyeApertureRatio(
  detection: LipDetection,
  outerIdx: number,
  innerIdx: number,
  upperIdx: number,
  lowerIdx: number
): number | null {
  const width = distance(point(detection, outerIdx), point(detection, innerIdx));
  const height = distance(point(detection, upperIdx), point(detection, lowerIdx));
  if (!width || !height) return null;
  return height / width;
}

function lidSpaceRatio(
  detection: LipDetection,
  upperLidIdx: number,
  browIdx: number,
  outerIdx: number,
  innerIdx: number
): number | null {
  const width = distance(point(detection, outerIdx), point(detection, innerIdx));
  const space = distance(point(detection, upperLidIdx), point(detection, browIdx));
  if (!width || !space) return null;
  return space / width;
}

export function analyzeFaceMorphology(detection: LipDetection): FaceMorphologyReport {
  const tuning: FaceMorphologyTuning = { ...DEFAULT_TUNING };

  const eyeOpen = average([
    eyeApertureRatio(detection, 33, 133, 159, 145),
    eyeApertureRatio(detection, 263, 362, 386, 374)
  ].filter((value): value is number => value !== null)) ?? 0.25;

  const lidSpace = average([
    lidSpaceRatio(detection, 159, 105, 33, 133),
    lidSpaceRatio(detection, 386, 334, 263, 362)
  ].filter((value): value is number => value !== null)) ?? 0.7;

  const mouthWidth = distance(point(detection, 61), point(detection, 291)) ?? 1;
  const upperLipHeight = distance(point(detection, 0), point(detection, 13)) ?? 0;
  const lowerLipHeight = distance(point(detection, 14), point(detection, 17)) ?? 0;
  const lipRatio = (upperLipHeight + lowerLipHeight) / Math.max(1, mouthWidth);

  const cheekWidth = faceWidth(detection);
  const cheekHeight = faceHeight(detection);
  const cheekRatio = cheekWidth / Math.max(1, cheekHeight);

  const eyeAperture =
    eyeOpen < 0.19 ? 'narrow' :
    eyeOpen > 0.30 ? 'open' :
    'balanced';
  const lidSpaceClass =
    lidSpace < 0.55 ? 'compact' :
    lidSpace > 0.82 ? 'roomy' :
    'balanced';
  const lipFullness =
    lipRatio < 0.13 ? 'thin' :
    lipRatio > 0.23 ? 'full' :
    'balanced';
  const cheekSpace =
    cheekRatio < 0.62 ? 'compact' :
    cheekRatio > 0.78 ? 'broad' :
    'balanced';

  if (eyeAperture === 'narrow' || lidSpaceClass === 'compact') {
    tuning.eyeOpacityScale *= 0.86;
    tuning.eyeShapeTightness = Math.max(tuning.eyeShapeTightness, 0.5);
    tuning.eyeFeatherScale *= 0.84;
    tuning.eyeSparkleScale *= 0.82;
  } else if (lidSpaceClass === 'roomy' && eyeAperture === 'open') {
    tuning.eyeOpacityScale *= 1.04;
    tuning.eyeShapeTightness = Math.max(tuning.eyeShapeTightness, 0.1);
    tuning.eyeFeatherScale *= 1.03;
  }

  if (lipFullness === 'thin') {
    tuning.lipOpacityScale *= 0.94;
    tuning.lipInnerHoleScale *= 1.06;
    tuning.lipFinishScale *= 0.9;
  } else if (lipFullness === 'full') {
    tuning.lipOpacityScale *= 1.02;
    tuning.lipFinishScale *= 1.04;
  }

  if (cheekSpace === 'compact') {
    tuning.cheekOpacityScale *= 0.9;
    tuning.cheekRadiusScale *= 0.88;
    tuning.cheekDrapeScale *= 0.9;
  } else if (cheekSpace === 'broad') {
    tuning.cheekRadiusScale *= 1.06;
    tuning.cheekDrapeScale *= 1.04;
  }

  tuning.cheekOpacityScale = clamp(tuning.cheekOpacityScale, 0.82, 1.08);
  tuning.cheekRadiusScale = clamp(tuning.cheekRadiusScale, 0.84, 1.08);
  tuning.cheekDrapeScale = clamp(tuning.cheekDrapeScale, 0.86, 1.06);
  tuning.eyeOpacityScale = clamp(tuning.eyeOpacityScale, 0.78, 1.06);
  tuning.eyeShapeTightness = clamp(tuning.eyeShapeTightness, 0, 0.62);
  tuning.eyeFeatherScale = clamp(tuning.eyeFeatherScale, 0.8, 1.05);
  tuning.eyeSparkleScale = clamp(tuning.eyeSparkleScale, 0.78, 1.04);
  tuning.lipOpacityScale = clamp(tuning.lipOpacityScale, 0.92, 1.04);
  tuning.lipInnerHoleScale = clamp(tuning.lipInnerHoleScale, 1, 1.12);
  tuning.lipFinishScale = clamp(tuning.lipFinishScale, 0.88, 1.06);

  return {
    eyeAperture,
    lidSpace: lidSpaceClass,
    lipFullness,
    cheekSpace,
    tuning
  };
}

export function analyzeLookMorphologyTuning(
  style: MakeupStyle,
  morphology: FaceMorphologyReport | null
): LookMorphologyTuningReport {
  const tuning: FaceMorphologyTuning = { ...DEFAULT_TUNING };
  const rules: string[] = [];
  if (!morphology) return { rules, tuning };

  const map = style.cosmeticMap;
  const risks = map?.riskControls.join(' ').toLowerCase() ?? '';
  const focus = new Set(map?.focus ?? []);
  const eyeFinish = style.kit?.eyeshadow.finish.toLowerCase() ?? '';
  const lipFinish = style.finish;
  const isDarkLip = style.hex.toLowerCase().match(/^#([0-5][0-9a-f])/) !== null;
  const isGloss = lipFinish === 'glossy';
  const isShimmer = eyeFinish.includes('shimmer') || eyeFinish.includes('metallic');

  if (morphology.lidSpace === 'compact' && (isShimmer || risks.includes('sticker') || risks.includes('crease'))) {
    tuning.eyeOpacityScale *= 0.88;
    tuning.eyeShapeTightness = Math.max(tuning.eyeShapeTightness, 0.58);
    tuning.eyeFeatherScale *= 0.82;
    tuning.eyeSparkleScale *= 0.78;
    rules.push('compact lid: shimmer/crease kept closer to lash line');
  }

  if (morphology.lidSpace === 'roomy' && focus.has('eyes') && !isShimmer) {
    tuning.eyeOpacityScale *= 1.05;
    tuning.eyeFeatherScale *= 1.04;
    rules.push('roomy lid: eye-focused matte look can breathe');
  }

  if (morphology.lipFullness === 'thin' && (isDarkLip || risks.includes('dark') || risks.includes('black'))) {
    tuning.lipOpacityScale *= 0.88;
    tuning.lipFinishScale *= 0.84;
    tuning.lipInnerHoleScale *= 1.08;
    rules.push('thin lips: dark coverage softened to preserve shape');
  } else if (morphology.lipFullness === 'thin' && isGloss) {
    tuning.lipOpacityScale *= 0.96;
    tuning.lipFinishScale *= 1.04;
    rules.push('thin lips: glossy look kept sheer and reflective');
  }

  if (morphology.lipFullness === 'full' && focus.has('lips')) {
    tuning.lipOpacityScale *= 1.03;
    tuning.lipFinishScale *= 1.03;
    rules.push('full lips: lip-focused look keeps its impact');
  }

  if (morphology.cheekSpace === 'compact' && (focus.has('lips') || focus.has('eyes'))) {
    tuning.cheekOpacityScale *= 0.86;
    tuning.cheekRadiusScale *= 0.86;
    tuning.cheekDrapeScale *= 0.88;
    rules.push('compact cheeks: blush reduced so focus stays intentional');
  }

  if (morphology.cheekSpace === 'broad' && focus.has('cheeks')) {
    tuning.cheekRadiusScale *= 1.08;
    tuning.cheekDrapeScale *= 1.05;
    rules.push('broad cheeks: cheek-focused look gets wider drape');
  }

  tuning.cheekOpacityScale = clamp(tuning.cheekOpacityScale, 0.82, 1.08);
  tuning.cheekRadiusScale = clamp(tuning.cheekRadiusScale, 0.84, 1.1);
  tuning.cheekDrapeScale = clamp(tuning.cheekDrapeScale, 0.86, 1.08);
  tuning.eyeOpacityScale = clamp(tuning.eyeOpacityScale, 0.78, 1.08);
  tuning.eyeShapeTightness = clamp(tuning.eyeShapeTightness, 0, 0.68);
  tuning.eyeFeatherScale = clamp(tuning.eyeFeatherScale, 0.78, 1.06);
  tuning.eyeSparkleScale = clamp(tuning.eyeSparkleScale, 0.74, 1.06);
  tuning.lipOpacityScale = clamp(tuning.lipOpacityScale, 0.84, 1.06);
  tuning.lipInnerHoleScale = clamp(tuning.lipInnerHoleScale, 1, 1.16);
  tuning.lipFinishScale = clamp(tuning.lipFinishScale, 0.8, 1.08);

  return { rules, tuning };
}
